/**
 * Demo API service for Try Now feature
 */

import { getAuthHeaders } from './authHeaders';

export interface StartDemoRequest {
  url: string;
}

export interface ViewportInfo {
  width: number;
  height: number;
  deviceScaleFactor?: number;
}

export interface DemoMetrics {
  totalOriginalBytes: number;
  totalTransformedBytes: number;
  totalReductionPercent: number;
  images: Array<{
    originalUrl: string;
    transformedUrl?: string;
    originalSize: number;
    transformedSize: number;
    originalFormat: string;
    optimized?: boolean;
    optimizationStatus?: 'optimized' | 'kept_original';
    statusMessage?: string;
  }>;
  skippedImages?: Array<{
    originalUrl: string;
    reason: string;
    message: string;
    originalSize?: number;
    transformedSize?: number;
  }>;
  discovery?: {
    totalFound: number;
    fromNetwork: number;
    fromDOM: number;
    fromStaticHTML: number;
  };
  outcomes?: {
    transformed: number;
    skippedFormat: number;
    skippedTooLarge: number;
    skippedDuplicate: number;
    failedDownload: number;
    failedTransform: number;
  };
  coverage?: {
    htmlGroupsTotal: number;
    htmlGroupsRewritten: number;
    coveragePercent: number;
  };
  viewport?: {
    key: string;
    label: string;
    width: number;
    height: number;
    deviceScaleFactor?: number;
  };
}

export interface DemoRecord {
  demoId: string;
  userId: string;
  crawlGroupId?: string;
  url: string;
  status: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  completedAt?: string;
  errorMessage?: string;
  imageCount: number;
  metrics?: DemoMetrics;
  s3DemoUrl?: string;
  viewportKey?: string;
  viewportLabel?: string;
  viewport?: ViewportInfo;
}

export interface ViewportStartDemo {
  demoId: string;
  viewportKey: string;
  viewportLabel: string;
  viewport: ViewportInfo;
  status: string;
}

export interface DemoStatusResponse {
  demoId: string;
  crawlGroupId?: string;
  url?: string;
  viewportKey?: string;
  viewportLabel?: string;
  viewport?: ViewportInfo;
  status: string;
  metrics?: DemoMetrics;
  imageCount: number;
  demoUrl?: string;
  errorMessage?: string;
}

export interface SharedResultResponse {
  crawlGroupId: string;
  url?: string;
  createdAt?: string;
  status: string;
  demos: DemoStatusResponse[];
}

const API_BASE = import.meta.env.VITE_API_ENDPOINT || '/api';

export const demoApi = {
  /**
   * Start a demo crawl
   */
  async startDemo(
    userId: string,
    url: string
  ): Promise<{ crawlGroupId: string; status: string; demos: ViewportStartDemo[] }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/demos/${userId}/start`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ url }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to start demo');
    }

    return response.json();
  },

  /**
   * Get demo status
   */
  async getDemoStatus(userId: string, demoId: string): Promise<DemoStatusResponse> {
    const headers = await getAuthHeaders();
    const response = await fetch(
      `${API_BASE}/demos/${userId}/${demoId}/status`,
      {
        method: 'GET',
        headers,
      }
    );

    if (!response.ok) {
      throw new Error('Failed to get demo status');
    }

    return response.json();
  },

  /**
   * List demos for a user with optional pagination
   */
  async listDemos(
    userId: string,
    limit?: number,
    nextToken?: string
  ): Promise<{ demos: DemoRecord[]; nextToken?: string }> {
    const headers = await getAuthHeaders();
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (nextToken) params.append('exclusiveStartKey', nextToken);

    const url = `${API_BASE}/demos/${userId}${params.toString() ? `?${params.toString()}` : ''}`;
    const response = await fetch(url, {
      method: 'GET',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to list demos');
    }

    return response.json();
  },

  async getSharedResult(crawlGroupId: string): Promise<SharedResultResponse> {
    const response = await fetch(`${API_BASE}/shared-results/${crawlGroupId}`, {
      method: 'GET',
    });

    if (!response.ok) {
      throw new Error('Failed to load shared result');
    }

    return response.json();
  },

  /**
   * Delete a demo
   */
  async deleteDemo(userId: string, demoId: string): Promise<{ success: boolean }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/demos/${userId}/${demoId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok) {
      throw new Error('Failed to delete demo');
    }

    return response.json();
  },

  /**
   * Start a guest demo (unauthenticated)
   */
  async startDemoGuest(
    url: string,
    turnstileToken: string
  ): Promise<{ crawlGroupId: string; status: string; demos: ViewportStartDemo[] }> {
    const response = await fetch(`${API_BASE}/demos/guest/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ url, turnstileToken }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to start guest demo');
    }

    return response.json();
  },

  /**
   * Migrate guest demos to authenticated user
   */
  async migrateGuestDemos(userId: string): Promise<{ migratedCount: number }> {
    const headers = await getAuthHeaders();
    const response = await fetch(`${API_BASE}/demos/${userId}/migrate-guest`, {
      method: 'POST',
      headers,
      body: JSON.stringify({}),
    });

    if (!response.ok) {
      throw new Error('Failed to migrate guest demos');
    }

    return response.json();
  },
};
